import { useState, type FormEvent } from 'react';
import { del, get, patch, post } from '../api';
import { useMe } from '../auth';
import { Alert, Card, fmtRelative, Modal, PageHeader, Spinner, useAction, useLoader } from '../components/ui';
import { canManage, type Role, type TeamUser } from '../types';

const ROLES: Role[] = ['owner', 'admin', 'viewer'];
const ROLE_HINT: Record<Role, string> = {
  owner: 'Full access, can manage owners',
  admin: 'Can change settings, keys, team and review messages',
  viewer: 'Read-only access',
};

function AddUserModal({ myRole, onClose, onAdded }: { myRole: Role; onClose: () => void; onAdded: () => void }) {
  const [form, setForm] = useState({ email: '', name: '', role: 'viewer' as Role, password: '' });
  const act = useAction();
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (form.password.length < 10) return act.setError('Initial password must be at least 10 characters');
    const ok = await act.run('add', () => post('/api/team', form));
    if (ok) onAdded();
  };
  return (
    <Modal title="Add team member" onClose={onClose}>
      <form className="form" onSubmit={submit}>
        <Alert>{act.error}</Alert>
        <label className="field">
          <span>Email</span>
          <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoFocus />
        </label>
        <label className="field">
          <span>Name</span>
          <input maxLength={120} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </label>
        <label className="field">
          <span>Role</span>
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as Role })}>
            {ROLES.filter((r) => canManage(myRole, r)).map((r) => (
              <option key={r} value={r}>
                {r} — {ROLE_HINT[r]}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Initial password</span>
          <input
            type="text"
            required
            minLength={10}
            autoComplete="off"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
          <span className="hint">At least 10 characters. Share it securely; they can change it after signing in.</span>
        </label>
        <div className="btn-row right">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" disabled={!!act.busy}>
            {act.busy ? 'Adding…' : 'Add member'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function TeamPage() {
  const { user: me, isAdmin } = useMe();
  const { data, error, loading, reload } = useLoader(() => get<{ users: TeamUser[] }>('/api/team'), []);
  const act = useAction();
  const [adding, setAdding] = useState(false);

  const changeRole = (u: TeamUser, role: Role) =>
    act.run(`role-${u.id}`, async () => {
      await patch(`/api/team/${u.id}`, { role });
      await reload(true);
    });
  const remove = (u: TeamUser) => {
    if (!window.confirm(`Remove ${u.email} from the organization?`)) return;
    void act.run(`del-${u.id}`, async () => {
      await del(`/api/team/${u.id}`);
      await reload(true);
    });
  };

  return (
    <>
      <PageHeader
        title="Team"
        subtitle="People who can access this dashboard."
        actions={
          isAdmin && (
            <button className="btn btn-primary" onClick={() => setAdding(true)}>
              Add member
            </button>
          )
        }
      />
      <Alert>{error ?? act.error}</Alert>
      <Card className="card-flush">
        {loading && !data ? (
          <Spinner />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Role</th>
                  <th>Last login</th>
                  <th>Added</th>
                  {isAdmin && <th />}
                </tr>
              </thead>
              <tbody>
                {data?.users.map((u) => {
                  const self = u.id === me.id;
                  const manageable = isAdmin && canManage(me.role, u.role);
                  return (
                    <tr key={u.id}>
                      <td>
                        <div className="strong">
                          {u.name || u.email.split('@')[0]} {self && <span className="badge badge-muted">you</span>}
                        </div>
                        <div className="muted small">{u.email}</div>
                      </td>
                      <td>
                        {manageable ? (
                          <select
                            className="select-sm"
                            value={u.role}
                            disabled={act.busy === `role-${u.id}`}
                            onChange={(e) => changeRole(u, e.target.value as Role)}
                            aria-label={`Role for ${u.email}`}
                          >
                            {ROLES.filter((r) => canManage(me.role, r)).map((r) => (
                              <option key={r} value={r}>
                                {r}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className={`role-pill role-${u.role}`}>{u.role}</span>
                        )}
                      </td>
                      <td className="nowrap muted">{fmtRelative(u.last_login_at)}</td>
                      <td className="nowrap muted">{fmtRelative(u.created_at)}</td>
                      {isAdmin && (
                        <td className="right">
                          {manageable && !self && (
                            <button className="btn btn-sm btn-danger-ghost" disabled={act.busy === `del-${u.id}`} onClick={() => remove(u)}>
                              Remove
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {adding && (
        <AddUserModal
          myRole={me.role}
          onClose={() => setAdding(false)}
          onAdded={() => {
            setAdding(false);
            void reload(true);
          }}
        />
      )}
    </>
  );
}
